import React, {useCallback, useEffect, useState} from 'react'
import {Alert, Badge, Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {URL as BACKEND_URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {parseId} from '../../../helpers'

interface Props {
  isOpen: boolean
  onClose: () => void
  stage: {id: string; name: string}
}

interface TagOption {
  id: string
  name: string
  color: string
}

interface WebhookConfig {
  id: number
  token: string
  google_key: string
  webhook_url: string
  is_active: boolean
  type_lead: 'PERSONAL' | 'COMERCIAL'
  lead_source_id: string | null
  lead_source_name: string | null
  campus_id: string | null
  campus_name: string | null
  tags: TagOption[]
  last_triggered_at: string | null
  leads_count: number
}

interface LeadSourceCategory {
  id: string
  name: string
  sources: {id: string; name: string}[]
}

interface Options {
  categories: LeadSourceCategory[]
  campuses: {id: string; name: string}[]
  tags: TagOption[]
}

type CopyField = 'url' | 'key' | null

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

export const ModalGoogleLeadsWebhook: React.FC<Props> = ({isOpen, onClose, stage}) => {
  const stageNumericId = Number(parseId(stage.id))

  const [config, setConfig] = useState<WebhookConfig | null>(null)
  const [options, setOptions] = useState<Options | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState<CopyField>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)

  // Form state
  const [categoryId, setCategoryId] = useState('')
  const [leadSourceId, setLeadSourceId] = useState('')
  const [campusId, setCampusId] = useState('')
  const [typeLead, setTypeLead] = useState<'PERSONAL' | 'COMERCIAL'>('PERSONAL')
  const [isActive, setIsActive] = useState(true)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const loadConfig = useCallback(async () => {
    if (!stageNumericId) return
    setLoadingConfig(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/google-leads/webhook/${stageNumericId}/`, {
        headers: authHeaders(),
      })
      if (res.status === 404) {
        setConfig(null)
      } else if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setLeadSourceId(data.lead_source_id || '')
        setCampusId(data.campus_id || '')
        setTypeLead(data.type_lead)
        setIsActive(data.is_active)
        setSelectedTagIds(data.tags.map((t) => t.id))
      } else {
        toast.error('Error loading webhook config')
      }
    } catch {
      toast.error('Network error loading config')
    } finally {
      setLoadingConfig(false)
    }
  }, [stageNumericId])

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/google-leads/webhook/options/`, {
        headers: authHeaders(),
      })
      if (res.ok) {
        const data: Options = await res.json()
        setOptions(data)
      }
    } catch {
      // non-blocking
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setConfig(null)
      setOptions(null)
      setCategoryId('')
      setLeadSourceId('')
      setCampusId('')
      setTypeLead('PERSONAL')
      setIsActive(true)
      setSelectedTagIds([])
      setConfirmDelete(false)
      setConfirmRegenerate(false)
      return
    }
    loadConfig()
    loadOptions()
  }, [isOpen, loadConfig, loadOptions])

  // Resolve category from loaded config's lead_source_id
  useEffect(() => {
    if (!config?.lead_source_id || !options) return
    for (const cat of options.categories) {
      if (cat.sources.some((s) => s.id === config.lead_source_id)) {
        setCategoryId(cat.id)
        break
      }
    }
  }, [config, options])

  const filteredSources =
    options?.categories.find((c) => c.id === categoryId)?.sources || []

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    )
  }

  const handleSave = async () => {
    if (!leadSourceId) {
      toast.warning('Please select a lead source')
      return
    }
    if (!campusId) {
      toast.warning('Please select a campus')
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/google-leads/webhook/${stageNumericId}/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          lead_source_id: leadSourceId,
          campus_id: campusId,
          type_lead: typeLead,
          is_active: isActive,
          tag_ids: selectedTagIds,
        }),
      })
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setSelectedTagIds(data.tags.map((t) => t.id))
        toast.success(config ? 'Webhook updated' : 'Webhook created')
      } else {
        const err = await res.json()
        toast.error(err?.error || 'Error saving webhook')
      }
    } catch {
      toast.error('Network error saving webhook')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/google-leads/webhook/${stageNumericId}/delete/`,
        {method: 'DELETE', headers: authHeaders()}
      )
      if (res.ok) {
        setConfig(null)
        setConfirmDelete(false)
        toast.success('Webhook deleted')
      } else {
        toast.error('Error deleting webhook')
      }
    } catch {
      toast.error('Network error deleting webhook')
    } finally {
      setDeleting(false)
    }
  }

  const handleRegenerate = async () => {
    setRegenerating(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/google-leads/webhook/${stageNumericId}/regenerate/`,
        {method: 'POST', headers: authHeaders()}
      )
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setConfirmRegenerate(false)
        toast.success('Token and key regenerated — update Google Ads settings')
      } else {
        toast.error('Error regenerating token')
      }
    } catch {
      toast.error('Network error regenerating token')
    } finally {
      setRegenerating(false)
    }
  }

  const copyToClipboard = async (text: string, field: CopyField) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(field)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error('Could not copy to clipboard')
    }
  }

  const isLoading = loadingConfig || loadingOptions

  return (
    <Modal show={isOpen} onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-2'>
          <svg width='20' height='20' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M43.6 20.2H24v7.2h11.3c-1.1 5.4-5.8 9.2-11.3 9.2-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.1-5.1C33.5 7.3 29 5.6 24 5.6 13.3 5.6 4.6 14.3 4.6 25S13.3 44.4 24 44.4c11 0 20.4-8 20.4-19.4 0-1.3-.1-2.6-.3-3.8z' fill='#4285F4'/>
            <path d='M6.3 15.1l6 4.4c1.6-4.5 5.8-7.7 11-7.7 3 0 5.7 1.1 7.8 2.9l5.1-5.1C33.5 7.3 29 5.6 24 5.6c-7.5 0-14 4.3-17.7 10.5z' fill='#EA4335'/>
            <path d='M24 44.4c5 0 9.4-1.7 12.8-4.5l-5.9-5c-1.8 1.3-4.1 2-6.9 2-5.5 0-10.2-3.7-11.9-8.9l-6 4.6C7.9 40.1 15.4 44.4 24 44.4z' fill='#34A853'/>
            <path d='M43.6 20.2H24v7.2h11.3c-.5 2.5-1.9 4.7-3.9 6.2l5.9 5c3.4-3.2 5.7-7.9 5.7-13.4 0-1.3-.1-2.6-.3-3.8z' fill='#FBBC04'/>
          </svg>
          Google Ads Webhook — {stage.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : (
          <>
            {config && (
              <>
                <Alert variant='info' className='py-2 mb-3'>
                  <small>
                    Copy these values into your Google Ads Lead Form &rarr; Webhook integration settings.
                  </small>
                </Alert>

                {/* Webhook URL */}
                <Form.Group className='mb-3'>
                  <Form.Label className='fw-bold'>Webhook URL</Form.Label>
                  <div className='input-group'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={config.webhook_url}
                      className='font-monospace'
                      style={{fontSize: '0.8rem'}}
                    />
                    <Button
                      variant={copied === 'url' ? 'success' : 'outline-secondary'}
                      onClick={() => copyToClipboard(config.webhook_url, 'url')}
                    >
                      <i className={`bi ${copied === 'url' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                    </Button>
                  </div>
                </Form.Group>

                {/* Google Key */}
                <Form.Group className='mb-4'>
                  <Form.Label className='fw-bold'>Key (Google Ads field)</Form.Label>
                  <div className='input-group'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={config.google_key}
                      className='font-monospace'
                    />
                    <Button
                      variant={copied === 'key' ? 'success' : 'outline-secondary'}
                      onClick={() => copyToClipboard(config.google_key, 'key')}
                    >
                      <i className={`bi ${copied === 'key' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                    </Button>
                  </div>
                </Form.Group>

                <hr />

                {/* Stats */}
                <div className='d-flex gap-3 mb-4'>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <div className='fw-bold fs-4'>{config.leads_count}</div>
                    <small className='text-muted'>Leads received</small>
                  </div>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <div className='fw-bold' style={{fontSize: '0.85rem'}}>
                      {config.last_triggered_at
                        ? new Date(config.last_triggered_at).toLocaleString()
                        : '—'}
                    </div>
                    <small className='text-muted'>Last triggered</small>
                  </div>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <Badge bg={config.is_active ? 'success' : 'secondary'} className='fs-6'>
                      {config.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <br />
                    <small className='text-muted'>Status</small>
                  </div>
                </div>

                <hr />
              </>
            )}

            {/* Config form */}
            <h6 className='mb-3'>{config ? 'Update settings' : 'Create webhook for this stage'}</h6>

            {/* Lead Source — 2 levels */}
            <Form.Group className='mb-3'>
              <Form.Label>Lead Source Category</Form.Label>
              <Form.Select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value)
                  setLeadSourceId('')
                }}
                disabled={loadingOptions}
              >
                <option value=''>— Select category —</option>
                {options?.categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Lead Source</Form.Label>
              <Form.Select
                value={leadSourceId}
                onChange={(e) => setLeadSourceId(e.target.value)}
                disabled={!categoryId || loadingOptions}
              >
                <option value=''>— Select source —</option>
                {filteredSources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Campus */}
            <Form.Group className='mb-3'>
              <Form.Label>Campus</Form.Label>
              <Form.Select
                value={campusId}
                onChange={(e) => setCampusId(e.target.value)}
                disabled={loadingOptions}
              >
                <option value=''>— Select campus —</option>
                {options?.campuses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Type lead */}
            <Form.Group className='mb-3'>
              <Form.Label>Lead type</Form.Label>
              <div className='d-flex gap-3'>
                <Form.Check
                  type='radio'
                  label='Personal'
                  id='type-personal'
                  checked={typeLead === 'PERSONAL'}
                  onChange={() => setTypeLead('PERSONAL')}
                />
                <Form.Check
                  type='radio'
                  label='Commercial'
                  id='type-comercial'
                  checked={typeLead === 'COMERCIAL'}
                  onChange={() => setTypeLead('COMERCIAL')}
                />
              </div>
            </Form.Group>

            {/* Tags */}
            {options && options.tags.length > 0 && (
              <Form.Group className='mb-3'>
                <Form.Label>Auto-assign tags</Form.Label>
                <div className='d-flex flex-wrap gap-2 p-2 border rounded'>
                  {options.tags.map((tag) => {
                    const selected = selectedTagIds.includes(tag.id)
                    return (
                      <button
                        key={tag.id}
                        type='button'
                        onClick={() => toggleTag(tag.id)}
                        style={{
                          background: selected ? (tag.color || '#6c757d') : 'transparent',
                          border: `2px solid ${tag.color || '#6c757d'}`,
                          borderRadius: '20px',
                          padding: '3px 12px',
                          fontSize: '0.82rem',
                          fontWeight: selected ? 600 : 400,
                          color: selected ? '#fff' : (tag.color || '#6c757d'),
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {selected && <i className='bi bi-check me-1' />}
                        {tag.name}
                      </button>
                    )
                  })}
                </div>
                {selectedTagIds.length > 0 && (
                  <small className='text-muted mt-1 d-block'>
                    {selectedTagIds.length} tag{selectedTagIds.length > 1 ? 's' : ''} selected — applied automatically to every lead from this webhook
                  </small>
                )}
              </Form.Group>
            )}

            {/* Active toggle (only when editing) */}
            {config && (
              <Form.Group className='mb-3'>
                <Form.Check
                  type='switch'
                  id='webhook-active-toggle'
                  label='Webhook active'
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              </Form.Group>
            )}

            {/* Danger zone */}
            {config && (
              <div className='border border-warning rounded p-3 mt-4'>
                <p className='fw-bold mb-2 text-warning'>
                  <i className='bi bi-exclamation-triangle me-2' />
                  Danger zone
                </p>

                {!confirmRegenerate ? (
                  <Button
                    variant='outline-warning'
                    size='sm'
                    className='me-2'
                    onClick={() => setConfirmRegenerate(true)}
                  >
                    <i className='bi bi-arrow-repeat me-1' />
                    Regenerate token & key
                  </Button>
                ) : (
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <small className='text-warning'>This invalidates the current URL. Update Google Ads.</small>
                    <Button
                      variant='warning'
                      size='sm'
                      onClick={handleRegenerate}
                      disabled={regenerating}
                    >
                      {regenerating ? <Spinner size='sm' animation='border' /> : 'Confirm regenerate'}
                    </Button>
                    <Button variant='link' size='sm' onClick={() => setConfirmRegenerate(false)}>
                      Cancel
                    </Button>
                  </div>
                )}

                {!confirmDelete ? (
                  <Button variant='outline-danger' size='sm' onClick={() => setConfirmDelete(true)}>
                    <i className='bi bi-trash me-1' />
                    Delete webhook
                  </Button>
                ) : (
                  <div className='d-flex align-items-center gap-2'>
                    <small className='text-danger'>Delete this webhook config?</small>
                    <Button variant='danger' size='sm' onClick={handleDelete} disabled={deleting}>
                      {deleting ? <Spinner size='sm' animation='border' /> : 'Yes, delete'}
                    </Button>
                    <Button variant='link' size='sm' onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
        {!isLoading && (
          <Button variant='primary' onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size='sm' animation='border' className='me-2' />
                Saving…
              </>
            ) : config ? (
              'Update webhook'
            ) : (
              'Create webhook'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
